import React, { useState, useEffect } from 'react';
import { Package, Factory, Warehouse, Building2, Euro } from 'lucide-react';

const PackagingFlowDiagram = () => {
  const sites = [
    { icon: Warehouse, label: 'WH', color: '#ff8b94' },
    { icon: Building2, label: 'Supplier 1', color: '#5b7bdb' },
    { icon: Warehouse, label: 'WH', color: '#ff8b94' },
    { icon: Factory, label: 'Factory', color: '#5b7bdb' },
    { icon: Warehouse, label: 'WH', color: '#ff8b94' },
    { icon: Building2, label: 'Supplier 2', color: '#5b7bdb' },
    { icon: Warehouse, label: 'WH', color: '#ff8b94' },
  ];

  const [packages, setPackages] = useState([
    { id: 1, position: 0, progress: 0, lost: false, showCost: false },
    { id: 2, position: 2, progress: 0, lost: false, showCost: false },
    { id: 3, position: 4, progress: 0, lost: false, showCost: false },
    { id: 4, position: 1, progress: 0.5, lost: false, showCost: false },
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setPackages(prevPackages => {
        return prevPackages.map(pkg => {
          // Si le package est perdu ou montre le coût, ne pas le bouger
          if (pkg.lost || pkg.showCost) {
            return pkg;
          }

          const newProgress = pkg.progress + 0.02;
          
          if (newProgress >= 1) {
            // 15% de chance de perdre le package à chaque changement de site
            if (Math.random() < 0.15 && pkg.position > 0) {
              // Marquer comme perdu
              setTimeout(() => {
                setPackages(prev => prev.map(p => 
                  p.id === pkg.id ? { ...p, lost: false, showCost: true, position: 0, progress: 0 } : p
                ));
                // Retirer le symbole Euro après 2 secondes
                setTimeout(() => {
                  setPackages(prev => prev.map(p => 
                    p.id === pkg.id ? { ...p, showCost: false } : p
                  ));
                }, 2000);
              }, 1000);
              return { ...pkg, lost: true, progress: 0 };
            }

            // Sinon, continuer normalement
            const nextPosition = (pkg.position + 1) % sites.length;
            return { ...pkg, position: nextPosition, progress: 0 };
          }
          
          return { ...pkg, progress: newProgress };
        });
      });
    }, 50);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="my-16 py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <h3 className="text-2xl font-bold text-center mb-12 text-gray-800">
          Returnable Packaging Flow Tracking
        </h3>
        
        <div className="relative py-8">
          {/* Desktop View - Horizontal Flow */}
          <div className="hidden lg:block relative" style={{ height: '280px', paddingTop: '60px', paddingBottom: '60px' }}>
            {/* Site Boxes */}
            <div className="flex items-center justify-center gap-4 relative z-10">
              {sites.map((site, index) => (
                <div key={index} className="flex flex-col items-center relative">
                  {/* Packages arrivant par le haut */}
                  <div className="absolute -top-20 left-1/2 transform -translate-x-1/2">
                    {packages.map(pkg => {
                      if (pkg.position === index && pkg.showCost && !pkg.lost) {
                        return (
                          <div 
                            key={`top-${pkg.id}`}
                            className="animate-drop-from-top relative"
                          >
                            <Package 
                              className="w-8 h-8 text-gray-700" 
                              style={{ 
                                filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))',
                              }}
                            />
                            <div className="absolute -top-6 -right-6 animate-euro-popup bg-red-500 rounded-full p-1.5">
                              <Euro className="w-5 h-5 text-white" />
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <div 
                    className="w-32 h-32 rounded-2xl flex flex-col items-center justify-center shadow-xl border-4 border-white"
                    style={{ backgroundColor: site.color }}
                  >
                    <site.icon className="w-12 h-12 text-white mb-2" />
                    <span className="text-white font-bold text-sm text-center px-2">{site.label}</span>
                  </div>

                  {/* Packages tombant en bas */}
                  <div className="absolute -bottom-20 left-1/2 transform -translate-x-1/2">
                    {packages.map(pkg => {
                      if (pkg.position === index && pkg.lost) {
                        return (
                          <div 
                            key={`bottom-${pkg.id}`}
                            className="animate-fall-down"
                          >
                            <Package 
                              className="w-8 h-8 text-gray-700 opacity-50" 
                              style={{ 
                                filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))',
                              }}
                            />
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Animated Packages Moving Between Sites */}
            <div className="absolute inset-0 pointer-events-none">
              {packages.map(pkg => {
                if (pkg.lost || pkg.showCost) return null;

                const currentSite = pkg.position;
                const nextSite = (pkg.position + 1) % sites.length;
                
                const siteSpacing = 150;
                const startX = 50 + (currentSite * siteSpacing);
                const endX = 50 + (nextSite * siteSpacing);
                const currentX = startX + ((endX - startX) * pkg.progress);
                
                return (
                  <div
                    key={pkg.id}
                    className="absolute transition-all duration-100"
                    style={{
                      left: `${currentX}px`,
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  >
                    <Package 
                      className="w-8 h-8 text-gray-700 drop-shadow-lg animate-bounce-slow" 
                      style={{ 
                        filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))',
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile/Tablet View - Vertical Flow */}
          <div className="flex lg:hidden flex-col items-center gap-4">
            {sites.map((site, index) => (
              <div key={index} className="flex items-center gap-4 w-full max-w-md relative">
                <div 
                  className="w-20 h-20 rounded-lg flex flex-col items-center justify-center shadow-lg flex-shrink-0 border-2 border-white"
                  style={{ backgroundColor: site.color }}
                >
                  <site.icon className="w-7 h-7 text-white mb-1" />
                  <span className="text-white font-semibold text-xs text-center">{site.label}</span>
                </div>
                
                <div className="flex-1 flex justify-center">
                  {packages.map(pkg => {
                    if (pkg.position === index && pkg.progress < 0.3) {
                      return (
                        <Package 
                          key={pkg.id}
                          className="w-5 h-5 animate-pulse" 
                          style={{ color: site.color }} 
                        />
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-md">
              <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
              <span className="text-sm font-medium text-gray-700">Real-Time Tracking Enabled</span>
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes fall-down {
          0% {
            transform: translateY(0) rotate(0deg) scale(1);
            opacity: 1;
          }
          50% {
            transform: translateY(30px) rotate(15deg) scale(0.8);
            opacity: 0.5;
          }
          100% {
            transform: translateY(60px) rotate(30deg) scale(0);
            opacity: 0;
          }
        }

        @keyframes drop-from-top {
          0% {
            transform: translateY(-60px);
            opacity: 0;
          }
          50% {
            transform: translateY(-30px);
            opacity: 0.7;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes euro-popup {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          20% {
            transform: scale(1.4);
            opacity: 1;
          }
          80% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(0.5);
            opacity: 0;
          }
        }

        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }

        .animate-fall-down {
          animation: fall-down 1s ease-out forwards;
        }

        .animate-drop-from-top {
          animation: drop-from-top 0.8s ease-out forwards;
        }

        .animate-euro-popup {
          animation: euro-popup 2s ease-out forwards;
        }
      `}} />
    </div>
  );
};

export default PackagingFlowDiagram;
